import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { CategoryScope } from '@/api/allocations';
import { resolveTokens } from '@/theme/theme';

const TITLE_ID = 'category-scope-title';
const BODY_ID = 'category-scope-body';

interface CategoryScopeDialogProps {
  /** The group's size (`groupSize` > 1). */
  count: number;
  /** True when the group's lines use different categories (`groupCategoryId` null). */
  mixed?: boolean;
  /** The category being applied; null = Uncategorized. Absent: the generic title. */
  categoryName?: string | null;
  onChoose: (scope: CategoryScope) => void;
  onClose: () => void;
}

/**
 * The line / group prompt (design A2, MM-D-65; restyled to C9 by TMMAPP-0108, audit 06 § C9, v2
 * L2386–2408): "Apply “{cat}” to…", then two option cards — "This entry only" (it stops following its
 * group) and "All N entries" (the group's category changes; entries set on their own keep theirs) —
 * and Cancel. Choosing a card acts and closes; there is no separate submit.
 */
export function CategoryScopeDialog({
  count,
  mixed = false,
  categoryName,
  onChoose,
  onClose,
}: CategoryScopeDialogProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const option = (scope: CategoryScope, label: string, note: string) => (
    <ButtonBase
      key={scope}
      onClick={() => onChoose(scope)}
      data-scope-option={scope}
      aria-labelledby={`category-scope-${scope}`}
      aria-describedby={`category-scope-${scope}-note`}
      sx={{
        display: 'block',
        width: '100%',
        textAlign: 'start',
        p: '12px 14px',
        border: `1px solid ${tokens.line}`,
        borderRadius: `${tokens.radius.control}px`,
        bgcolor: tokens.surfaceRaised,
        color: tokens.text,
        '&:hover': {
          borderColor: tokens.brandBoundary,
          backgroundImage: `linear-gradient(${tokens.selected}, ${tokens.selected})`,
        },
        '&.Mui-focusVisible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
      }}
    >
      <Typography
        id={`category-scope-${scope}`}
        component="span"
        sx={{ display: 'block', fontSize: 14, fontWeight: 500 }}
      >
        {label}
      </Typography>
      <Typography
        id={`category-scope-${scope}-note`}
        component="span"
        sx={{ display: 'block', fontSize: 12, lineHeight: 1.5, mt: '2px', color: tokens.text2 }}
      >
        {note}
      </Typography>
    </ButtonBase>
  );
  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby={TITLE_ID}
      aria-describedby={BODY_ID}
      maxWidth="xs"
      fullWidth
      sx={{ '& .MuiDialog-paper': { maxWidth: 420 } }}
    >
      <DialogTitle id={TITLE_ID}>
        {categoryName === undefined
          ? t('envelope.scopeTitle')
          : t('envelope.scopeTitleNamed', { name: categoryName ?? t('envelope.uncategorized') })}
      </DialogTitle>
      <DialogContent>
        <Typography id={BODY_ID} variant="body2" sx={{ color: tokens.text2, lineHeight: 1.6 }}>
          {t('envelope.scopeBody', { count })}
        </Typography>
        {mixed && (
          <Typography variant="body2" sx={{ color: tokens.text2, mt: 1 }}>
            {t('envelope.groupMixed')}
          </Typography>
        )}
        <Stack spacing={1} sx={{ mt: 2.25 }}>
          {option('LINE', t('envelope.scopeLine'), t('envelope.scopeLineNote'))}
          {option('GROUP', t('envelope.scopeGroup', { count }), t('envelope.scopeGroupNote'))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>{t('common.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
}
